import React from 'react';

import { useIsMobileV2 } from 'src/application/hooks/useIsMobile';

import { goToSelectedSequencedFlowContentItem } from '../../../../actions/contentItemActions';
import { ProgressBarStepModel } from '../../../../components/ProgressBar/model';
import { ProgressBar } from '../../../../components/ProgressBar/ProgressBar';
import { HistoryItem } from '../../../../store/reducers/currentSequencedFlowPositionReducer';
import { getItemByIdTypedSelector } from '../../../../store/reducers/items/itemsSelectors';
import { getSequencedFlowStructureSelector } from '../../../../store/selectors/getSequencedFlowStructureSelector';
import { useStudentSelector } from '../../../../store/selectors/useStudentSelector';
import { AssertionException } from '../../../../utils/log';
import { evaluateItemSelector } from '../../../tracking/services/CompletionService';
import { useSequencedContentFlowContext } from '../../context/SequencedContentFlowContext';
import { calculateHistoryItems } from '../../utils';
import {
  isSection,
  isSequencedFlowSectionOrStep,
  isSequencedFlowStep,
  Section,
  TrackingStatusEnum,
} from '@ael/core-api';
import { useDispatch } from 'react-redux';

interface SequencedFlowProgressBarProps {
  currentContentItemId: string;
}

export const SequencedFlowProgressBar: React.FC<SequencedFlowProgressBarProps> = ({ currentContentItemId }) => {
  const { sequencedFlowItem, currentSection } = useSequencedContentFlowContext();
  const getItemByIdTyped = useStudentSelector(getItemByIdTypedSelector);
  const dispatch = useDispatch();
  const getSequencedFlowStructure = useStudentSelector(getSequencedFlowStructureSelector);
  const evaluate = useStudentSelector(evaluateItemSelector);
  const isMobile = useIsMobileV2();

  const contentItems = React.useMemo(
    () =>
      isSection(currentSection)
        ? currentSection.children.map((id: string) => getItemByIdTyped(id, isSequencedFlowSectionOrStep)).toArray()
        : currentSection.contentItems.map((id: string) => getItemByIdTyped(id, isSequencedFlowSectionOrStep)).toArray(),
    [currentSection, getItemByIdTyped]
  );

  const progressBarSteps: ProgressBarStepModel[] = [];
  const sections: Section[] = [];
  contentItems.forEach((item) =>
    isSequencedFlowStep(item)
      ? progressBarSteps.push({
          id: item.id,
          selected: item.id === currentContentItemId,
          disabled: false,
        })
      : sections.push(item)
  );

  if (sections.length > 0) {
    const isActive = sections.some((section: Section) => {
      const trackingStatus = evaluate(section);
      return trackingStatus === TrackingStatusEnum.Completed || trackingStatus === TrackingStatusEnum.InProgress;
    });

    progressBarSteps.push({
      id: sections[0].id,
      selected: sections[0].id === currentContentItemId,
      disabled: !isActive,
      isSplit: true,
    });
  }

  if (isSection(currentSection)) {
    progressBarSteps.unshift({
      id: currentSection.id,
      selected: false,
      disabled: false,
      isSplit: true,
    });
  }

  const goToStep = (contentItemId: string) => {
    const sequencedFlowStructure = getSequencedFlowStructure(sequencedFlowItem.id);
    if (!sequencedFlowStructure) {
      throw AssertionException('Sequenced flow structure cannot be undefined.');
    }
    const currentHistoryItem = sequencedFlowStructure.find(
      (historyItem: HistoryItem) => historyItem?.contentItemId === contentItemId
    );
    if (!currentHistoryItem) {
      throw AssertionException('Current history item cannot be undefined.');
    }
    const history = calculateHistoryItems(sequencedFlowStructure, currentHistoryItem);
    dispatch(
      goToSelectedSequencedFlowContentItem(
        currentHistoryItem.contentItemId,
        currentHistoryItem.sectionId,
        isMobile,
        history
      )
    );
  };

  return <ProgressBar steps={progressBarSteps} onClickOnStep={goToStep} />;
};
